import { useFrame } from '@react-three/fiber';
import gsap from 'gsap';
import { memo, useRef } from 'react';
import { Group, Mesh, Vector3 } from 'three';

import Earth, { EarthRef } from '0_canvas/Earth/Earth';
import Stars from '0_canvas/Stars/Stars';
import { EARTH_STAGE, useThreeStore } from 'state/threeStore';

import {
  centerAnimation,
  earthRevealAnimation,
  rotateEarth,
  rotateToAustralia,
} from './EarthScene.animations';
import { AUSTRALIA_ROTATION } from './EarthScene.utils';

import useMatrixUpdateHandler from 'hooks/useMatrixUpdateHandler';

const INITIAL_POSITION = new Vector3(0, -200, 0);
const LIGHT_SOURCE_POSITION_HALF = new Vector3(8000, 0, 0);
const LIGHT_SOURCE_POSITION_FULL = new Vector3(0, 0, 1000);

const realWorldPosition = new Vector3();

const EarthScene = () => {
  const earthRef = useRef<EarthRef>(null!);
  const stageRef = useRef<EARTH_STAGE | undefined>(undefined);
  const lightSourceRef = useRef<Mesh>(null!);
  const starsRef = useRef<Group>(null!);

  useFrame(() => {
    const {
      earth: { stage },
      setEarth,
    } = useThreeStore.getState();
    if (stage === stageRef.current) return;

    // Don't consume the stage change before the refs the animations below
    // drive actually exist, or the stage is marked as handled and its
    // animation never runs.
    if (!earthRef.current || !lightSourceRef.current) return;

    stageRef.current = stage;

    switch (stage) {
      case 'LANDING':
        earthRevealAnimation(earthRef.current).then(() => {
          gsap.timeline().to(lightSourceRef.current.position, {
            ...LIGHT_SOURCE_POSITION_HALF,
            duration: 2,
          });
          rotateEarth(earthRef.current);

          earthRef.current.playWatchAgain();
        });
        break;
      case 'CENTERED':
        centerAnimation(earthRef.current).then();
        break;
      case 'DESTINATION_TRANSITION_START':
        earthRef.current.playWatchAgain();
        gsap.timeline().to(lightSourceRef.current.position, {
          ...LIGHT_SOURCE_POSITION_FULL,
          duration: 1.5,
        });
        rotateToAustralia(earthRef.current, () => {
          setEarth({ stage: 'DESTINATION_TRANSITION_END' });
        });
        break;
      case 'OUTRO_TRANSITION_END':
        rotateEarth(earthRef.current);
        earthRef.current.playOutro();
        break;
    }
  });

  useFrame(() => {
    const earth = earthRef.current?.earth.current;
    if (!earth) return;

    const { isRendered } = useThreeStore.getState().earth;
    (earth as any).visible = isRendered;
  });

  useFrame(() => {
    // earthRef is empty for the frames between a remount and the ref being
    // reattached; throwing here aborts the rest of the frame's subscribers.
    if (!lightSourceRef.current || !earthRef.current?.sunDirection) return;

    lightSourceRef.current.getWorldPosition(realWorldPosition);

    earthRef.current.sunDirection.current
      .copy(realWorldPosition)
      .normalize()
      .negate();
  });

  useMatrixUpdateHandler(
    () => !!useThreeStore.getState().earth.isRendered,
    earthRef.current?.earth.current
  );
  useMatrixUpdateHandler(() => starsRef.current.visible, starsRef.current);

  return (
    <group name="earthScene">
      <Earth
        ref={earthRef}
        position={INITIAL_POSITION}
        visible={true}
        rotation={[
          AUSTRALIA_ROTATION.x,
          AUSTRALIA_ROTATION.y,
          AUSTRALIA_ROTATION.z,
        ]}
      />

      <group ref={starsRef}>
        <Stars rotationSpeed={0.0001} />
      </group>

      <mesh ref={lightSourceRef}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color={0x00ff00} transparent opacity={0} />
      </mesh>
    </group>
  );
};

export default memo(EarthScene);
